import { products as initialProducts } from './data/products';

// Fonction pour obtenir les produits depuis localStorage
const getProductsFromStorage = () => {
  const savedProducts = localStorage.getItem('farmProducts');
  if (savedProducts) {
    try {
      return JSON.parse(savedProducts);
    } catch (err) {
      console.error('Erreur lors du parsing des produits:', err);
      return initialProducts;
    }
  }
  return initialProducts;
};

// Fonction pour sauvegarder les produits dans localStorage
const saveProductsToStorage = (products) => {
  localStorage.setItem('farmProducts', JSON.stringify(products));
  console.log('💾 Produits sauvegardés:', products.length);
  
  // Déclencher un événement personnalisé pour la synchronisation
  window.dispatchEvent(new CustomEvent('productsUpdated', {
    detail: { products, count: products.length }
  }));
};

// Base de données des utilisateurs (en mémoire)
let users = [
  {
    id: 1,
    email: 'admin@lafermeancienne.fr',
    password: 'AdminFerme2024!',
    firstName: 'Administrateur',
    lastName: 'Ferme',
    role: 'admin',
    createdAt: new Date().toISOString()
  }
];

const dataProvider = {
  getList: (resource, params) => {
    if (resource === 'products') {
      const products = getProductsFromStorage();
      const { page = 1, perPage = 10 } = params.pagination || {};
      const { field = 'id', order = 'ASC' } = params.sort || {};
      const { q, category, featured } = params.filter || {};

      let filteredProducts = [...products];

      // Filtrage par recherche
      if (q) {
        filteredProducts = filteredProducts.filter(product =>
          product.name.toLowerCase().includes(q.toLowerCase()) ||
          product.category.toLowerCase().includes(q.toLowerCase())
        );
      }

      // Filtrage par catégorie
      if (category) {
        filteredProducts = filteredProducts.filter(product =>
          product.category === category
        );
      }

      // Filtrage par produit vedette
      if (featured !== undefined) {
        filteredProducts = filteredProducts.filter(product =>
          product.featured === featured
        );
      }

      // Tri
      filteredProducts.sort((a, b) => {
        if (order === 'ASC') {
          return a[field] > b[field] ? 1 : -1;
        }
        return a[field] < b[field] ? 1 : -1;
      });

      // Pagination
      const start = (page - 1) * perPage;
      const end = start + perPage;
      const paginatedProducts = filteredProducts.slice(start, end);

      return Promise.resolve({
        data: paginatedProducts,
        total: filteredProducts.length,
      });
    }
    
    if (resource === 'users') {
      const { page = 1, perPage = 10 } = params.pagination || {};
      const { field = 'id', order = 'ASC' } = params.sort || {};
      const { q, role } = params.filter || {};

      let filteredUsers = [...users];

      // Filtrage par recherche
      if (q) {
        filteredUsers = filteredUsers.filter(user =>
          user.firstName.toLowerCase().includes(q.toLowerCase()) ||
          user.lastName.toLowerCase().includes(q.toLowerCase()) ||
          user.email.toLowerCase().includes(q.toLowerCase())
        );
      }

      // Filtrage par rôle
      if (role) {
        filteredUsers = filteredUsers.filter(user => user.role === role);
      }

      // Tri
      filteredUsers.sort((a, b) => {
        if (order === 'ASC') {
          return a[field] > b[field] ? 1 : -1;
        }
        return a[field] < b[field] ? 1 : -1;
      });

      // Pagination
      const start = (page - 1) * perPage;
      const end = start + perPage;
      const paginatedUsers = filteredUsers.slice(start, end);

      return Promise.resolve({
        data: paginatedUsers,
        total: filteredUsers.length,
      });
    }
    
    return Promise.reject(new Error(`Resource ${resource} not supported`));
  },

  getOne: (resource, params) => {
    if (resource === 'products') {
      const products = getProductsFromStorage();
      const product = products.find(p => p.id === params.id);
      if (product) {
        return Promise.resolve({ data: product });
      }
      return Promise.reject(new Error('Product not found'));
    }
    if (resource === 'users') {
      const user = users.find(u => u.id === params.id);
      if (user) {
        return Promise.resolve({ data: user });
      }
      return Promise.reject(new Error('User not found'));
    }
    return Promise.reject(new Error(`Resource ${resource} not supported`));
  },

  getMany: (resource, params) => {
    if (resource === 'products') {
      const products = getProductsFromStorage();
      const requestedProducts = products.filter(p => params.ids.includes(p.id));
      return Promise.resolve({ data: requestedProducts });
    }
    if (resource === 'users') {
      const requestedUsers = users.filter(u => params.ids.includes(u.id));
      return Promise.resolve({ data: requestedUsers });
    }
    return Promise.reject(new Error(`Resource ${resource} not supported`));
  },

  getManyReference: (resource, params) => {
    return Promise.resolve({ data: [], total: 0 });
  },

  create: (resource, params) => {
    if (resource === 'products') {
      const products = getProductsFromStorage();
      const nextId = Math.max(...products.map(p => p.id), 0) + 1;
      
      const newProduct = {
        ...params.data,
        id: nextId,
        createdAt: new Date().toISOString()
      };
      
      const updatedProducts = [...products, newProduct];
      saveProductsToStorage(updatedProducts);
      
      console.log('✅ Produit créé:', newProduct.name, '(ID:', newProduct.id, ')');
      
      return Promise.resolve({ data: newProduct });
    }
    return Promise.reject(new Error(`Resource ${resource} not supported`));
  },

  update: (resource, params) => {
    if (resource === 'products') {
      const products = getProductsFromStorage();
      const index = products.findIndex(p => p.id === params.id);
      
      if (index !== -1) {
        const updatedProducts = [...products];
        updatedProducts[index] = { 
          ...updatedProducts[index], 
          ...params.data,
          updatedAt: new Date().toISOString()
        };
        
        saveProductsToStorage(updatedProducts);
        
        console.log('✅ Produit modifié:', updatedProducts[index].name);
        
        return Promise.resolve({ data: updatedProducts[index] });
      }
      return Promise.reject(new Error('Product not found'));
    }
    return Promise.reject(new Error(`Resource ${resource} not supported`));
  },

  updateMany: (resource, params) => {
    if (resource === 'products') {
      const products = getProductsFromStorage();
      const updatedProducts = [...products];
      const updatedIds = [];
      
      params.ids.forEach(id => {
        const index = updatedProducts.findIndex(p => p.id === id);
        if (index !== -1) {
          updatedProducts[index] = { 
            ...updatedProducts[index], 
            ...params.data,
            updatedAt: new Date().toISOString()
          };
          updatedIds.push(id);
        }
      });
      
      saveProductsToStorage(updatedProducts);
      
      return Promise.resolve({ data: updatedIds });
    }
    return Promise.reject(new Error(`Resource ${resource} not supported`));
  },

  delete: (resource, params) => {
    if (resource === 'products') {
      const products = getProductsFromStorage();
      const index = products.findIndex(p => p.id === params.id);
      
      if (index !== -1) {
        const deletedProduct = products[index];
        const updatedProducts = products.filter(p => p.id !== params.id);
        
        saveProductsToStorage(updatedProducts);
        
        console.log('❌ Produit supprimé:', deletedProduct.name);
        
        return Promise.resolve({ data: deletedProduct });
      }
      return Promise.reject(new Error('Product not found'));
    }
    return Promise.reject(new Error(`Resource ${resource} not supported`));
  },

  deleteMany: (resource, params) => {
    if (resource === 'products') {
      const products = getProductsFromStorage();
      const updatedProducts = products.filter(p => !params.ids.includes(p.id));
      const deletedIds = params.ids.filter(id => 
        products.some(p => p.id === id)
      );
      
      saveProductsToStorage(updatedProducts);
      
      return Promise.resolve({ data: deletedIds });
    }
    return Promise.reject(new Error(`Resource ${resource} not supported`));
  },
};

export default dataProvider;
